// src/pages/components/PayModal.jsx
import { useState, useEffect, useMemo } from "react";
import {
  X,
  Plus,
  Trash2,
  Lock,
  CheckCircle2,
  Banknote,
  Landmark,
} from "lucide-react";
import {
  DEFAULT_RATES,
  fetchSellerRegisteredState,
  fetchSellerStateRates,
} from "../../services/stateRates";
import { calculateSellerOrderTotals } from "../utils/calculationutil";

// ─────────────────────────────────────────────────────────────────────────────
// PayModal – Operator pays the SELLER.
//
// Each order carries `sellerPayment` = "Metal" | "RTGS" (or absent → RTGS).
// Totals match the seller table / seller product details page:
//   • fine weight  → calculateSellerOrderTotals().fineWeight
//   • amount       → calculateSellerOrderTotals().totalAmount
//
// On "Confirm Settlement" → calls onConfirm(settlementPayload).
// ─────────────────────────────────────────────────────────────────────────────

function PayModal({ seller, group, onClose, onConfirm }) {
  const { orders = [] } = group;

  const parseNum = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
  };

  const round3 = (value) => parseFloat(parseNum(value).toFixed(3));
  const round2 = (value) => parseFloat(parseNum(value).toFixed(2));

  // ──────────────────────────────────────────────────────────────────────────
  // PAYMENT METHOD DETECTION  (field: sellerPayment)
  // ──────────────────────────────────────────────────────────────────────────
  const hasExplicitMetal = orders.some((o) => o.sellerPayment === "Metal");
  const hasExplicitRTGS  = orders.some((o) => o.sellerPayment === "RTGS");
  // If no order has an explicit sellerPayment → treat everything as RTGS.
  const hasRTGS  = hasExplicitRTGS || !hasExplicitMetal;
  const hasMetal = hasExplicitMetal;
  const rtgsOrders = useMemo(() => {
    if (!hasExplicitRTGS && !hasExplicitMetal) return orders;        // all RTGS
    return orders.filter((o) => o.sellerPayment === "RTGS");
  }, [orders, hasExplicitRTGS, hasExplicitMetal]);
  const metalOrders = useMemo(
    () => orders.filter((o) => o.sellerPayment === "Metal"),
    [orders],
  );
  // ──────────────────────────────────────────────────────────────────────────
  // LIVE RATES
  // ──────────────────────────────────────────────────────────────────────────
  const [liveRates, setLiveRates]     = useState({ ...DEFAULT_RATES });
  const [ratesLoading, setRatesLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    const loadRates = async () => {
      setRatesLoading(true);
      try {
        const firstOrder = orders[0] || {};
        const sellerId = group.sellerId || firstOrder.sellerId || seller;
        const stateFromOrder =
          group.sellerState ||
          group.state ||
          firstOrder.sellerState ||
          firstOrder.state ||
          firstOrder.contactState;
        const sellerState = stateFromOrder || await fetchSellerRegisteredState(sellerId);
        const segmentRaw = firstOrder.segment || firstOrder.category || "gold";
        const segment = String(segmentRaw).toLowerCase().includes("silver")
          ? "silver"
          : String(segmentRaw).toLowerCase().includes("platinum")
            ? "platinum"
            : "gold";
        const rates = await fetchSellerStateRates(sellerState, segment);
        
        // Debug: Log rates being used in PayModal
        console.log("[PayModal] Rates loaded for seller payment:", {
          sellerId,
          sellerState,
          stateFromOrder,
          segment,
          rates: {
            RTGS_SALE: rates.RTGS_SALE_RATE,
            RTGS_PURCHASE: rates.RTGS_PURCHASE_RATE,
            METAL_SALE: rates.METAL_SALE_RATE,
            METAL_PURCHASE: rates.METAL_PURCHASE_RATE,
          },
          indianBase: {
            RTGS_SALE: rates.INDIA_RTGS_SALE,
            RTGS_PURCHASE: rates.INDIA_RTGS_PURCHASE,
            METAL_SALE: rates.INDIA_METAL_SALE,
            METAL_PURCHASE: rates.INDIA_METAL_PURCHASE,
          },
          adjustment: rates.ADJUSTMENT,
        });
        
        if (!cancelled) setLiveRates(rates);
      } catch (err) {
        console.error("[PayModal] Failed to fetch seller state rates:", err);
      } finally {
        if (!cancelled) setRatesLoading(false);
      }
    };
    loadRates();
    return () => { cancelled = true; };
  }, [group, orders, seller]);

  // ──────────────────────────────────────────────────────────────────────────
  // RTGS METAL ENTRIES
  // ──────────────────────────────────────────────────────────────────────────

  const [rtgsMetalEntries, setRtgsMetalEntries] = useState([
    { gross: "", purity: "", billNo: "" },
  ]);
  const [rtgsCashAmount,          setRtgsCashAmount]          = useState("");
  const [rtgsCollectionReceipt,   setRtgsCollectionReceipt]   = useState("");
  const [rtgsBankingEntries,      setRtgsBankingEntries]      = useState([
    { amount: "", utr: "" },
  ]);

  // ──────────────────────────────────────────────────────────────────────────
  // METAL BUCKET
  // ──────────────────────────────────────────────────────────────────────────

  const [mtlMetalEntries, setMtlMetalEntries] = useState([
    { gross: "", purity: "", billNo: "" },
  ]);
  const [mtlCashAmount, setMtlCashAmount] = useState("");

  // ──────────────────────────────────────────────────────────────────────────
  // PROCESSING
  // ──────────────────────────────────────────────────────────────────────────

  const [isProcessing, setIsProcessing] = useState(false);

  // ──────────────────────────────────────────────────────────────────────────
  // SELLER DISPLAY TOTALS  (same formula as seller table / product details)
  // ──────────────────────────────────────────────────────────────────────────

  const sumSellerDisplay = (arr) =>
    arr.reduce(
      (acc, order) => {
        const s = calculateSellerOrderTotals(order);
        return {
          fineWeight:  round3(acc.fineWeight  + (s.fineWeight  || 0)),
          totalAmount: round2(acc.totalAmount + (s.totalAmount || 0)),
        };
      },
      { fineWeight: 0, totalAmount: 0 },
    );

  const rtgsTotals  = sumSellerDisplay(rtgsOrders);
  const metalTotals = sumSellerDisplay(metalOrders);

  // ──────────────────────────────────────────────────────────────────────────
  // BASE DUES
  // ──────────────────────────────────────────────────────────────────────────

  const rtgsMetalDue = hasRTGS ? rtgsTotals.fineWeight  : 0;
  const rtgsCashDue  = hasRTGS ? rtgsTotals.totalAmount : 0;
  const mtlMetalDue  = metalTotals.fineWeight;
  const mtlCashDue   = metalTotals.totalAmount;

  // ──────────────────────────────────────────────────────────────────────────
  // FINE ENTERED
  // ──────────────────────────────────────────────────────────────────────────

  const rtgsFineEntered = round3(
    rtgsMetalEntries.reduce(
      (sum, e) => sum + (parseNum(e.gross) * parseNum(e.purity)) / 100,
      0,
    ),
  );

  const mtlFineEntered = round3(
    mtlMetalEntries.reduce(
      (sum, e) => sum + (parseNum(e.gross) * parseNum(e.purity)) / 100,
      0,
    ),
  );

  // ──────────────────────────────────────────────────────────────────────────
  // CASH ENTERED
  // ──────────────────────────────────────────────────────────────────────────

  const rtgsPhysicalAmount = round2(parseNum(rtgsCashAmount));
  const rtgsBankingTotal   = round2(
    rtgsBankingEntries.reduce((sum, e) => sum + parseNum(e.amount), 0),
  );
  const rtgsAmountEntered  = round2(rtgsPhysicalAmount + rtgsBankingTotal);
  const mtlAmountEntered   = parseNum(mtlCashAmount);

  // ──────────────────────────────────────────────────────────────────────────
  // RTGS SURPLUS / DEFICIT
  // ──────────────────────────────────────────────────────────────────────────

  let rtgsRemainingFine  = Math.max(0, round3(rtgsMetalDue - rtgsFineEntered));
  let rtgsSurplusFine    = Math.max(0, round3(rtgsFineEntered - rtgsMetalDue));
  let rtgsRemainingMc    = Math.max(0, round2(rtgsCashDue - rtgsAmountEntered));
  let rtgsSurplusAmount  = Math.max(0, round2(rtgsAmountEntered - rtgsCashDue));

  let rtgsEffectiveFineDue = rtgsRemainingFine;
  let rtgsEffectiveMcDue   = rtgsRemainingMc;
  let rtgsSpilloverFine    = 0;
  let rtgsSpilloverAmount  = 0;

  // Fine surplus → converts at PURCHASE RATE → reduces MC due
  if (rtgsSurplusFine > 0) {
    const purchaseRate = parseNum(liveRates.RTGS_PURCHASE_RATE);
    if (purchaseRate > 0) {
      const mcReduction = rtgsSurplusFine * purchaseRate;
      if (mcReduction >= rtgsRemainingMc) {
        rtgsEffectiveMcDue  = 0;
        const leftover      = mcReduction - rtgsRemainingMc;
        rtgsSpilloverFine   = round3(leftover / purchaseRate);
      } else {
        rtgsEffectiveMcDue = round2(rtgsRemainingMc - mcReduction);
      }
    }
  }
  // Cash surplus → converts at SALE RATE → reduces fine due
  else if (rtgsSurplusAmount > 0) {
    const saleRate = parseNum(liveRates.RTGS_SALE_RATE);
    if (saleRate > 0) {
      const fineReduction = rtgsSurplusAmount / saleRate;
      if (fineReduction >= rtgsRemainingFine) {
        rtgsEffectiveFineDue = 0;
        const leftover       = fineReduction - rtgsRemainingFine;
        rtgsSpilloverAmount  = round2(leftover * saleRate);
      } else {
        rtgsEffectiveFineDue = round3(rtgsRemainingFine - fineReduction);
      }
    }
  }

  const isRtgsCompleted =
    !hasRTGS || (rtgsEffectiveFineDue === 0 && rtgsEffectiveMcDue === 0);

  // ──────────────────────────────────────────────────────────────────────────
  // METAL BUCKET + RTGS SPILLOVER
  // ──────────────────────────────────────────────────────────────────────────

  let mtlMetalAfterSpill = mtlMetalDue;
  let mtlCashAfterSpill  = mtlCashDue;

  if (rtgsSpilloverFine > 0) {
    if (rtgsSpilloverFine >= mtlMetalDue) {
      mtlMetalAfterSpill = 0;
      const remainingSpillFine = rtgsSpilloverFine - mtlMetalDue;
      const purchaseRate = parseNum(liveRates.METAL_PURCHASE_RATE);
      if (purchaseRate > 0) {
        const spillMcRed = remainingSpillFine * purchaseRate;
        mtlCashAfterSpill = Math.max(0, round2(mtlCashDue - spillMcRed));
      }
    } else {
      mtlMetalAfterSpill = round3(mtlMetalDue - rtgsSpilloverFine);
    }
  } else if (rtgsSpilloverAmount > 0) {
    if (rtgsSpilloverAmount >= mtlCashDue) {
      mtlCashAfterSpill = 0;
      const remainingSpillAmt = rtgsSpilloverAmount - mtlCashDue;
      const saleRate = parseNum(liveRates.METAL_SALE_RATE);
      if (saleRate > 0) {
        const spillFineRed = remainingSpillAmt / saleRate;
        mtlMetalAfterSpill = Math.max(0, round3(mtlMetalDue - spillFineRed));
      }
    } else {
      mtlCashAfterSpill = round2(mtlCashDue - rtgsSpilloverAmount);
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // METAL BUCKET SURPLUS / DEFICIT
  // ──────────────────────────────────────────────────────────────────────────

  let mtlRemainingFine  = Math.max(0, round3(mtlMetalAfterSpill - mtlFineEntered));
  let mtlSurplusFine    = Math.max(0, round3(mtlFineEntered - mtlMetalAfterSpill));
  let mtlRemainingMc    = Math.max(0, round2(mtlCashAfterSpill - mtlAmountEntered));
  let mtlSurplusAmount  = Math.max(0, round2(mtlAmountEntered - mtlCashAfterSpill));

  let mtlEffectiveFineDue = mtlRemainingFine;
  let mtlEffectiveMcDue   = mtlRemainingMc;

  if (mtlSurplusFine > 0) {
    const purchaseRate = parseNum(liveRates.METAL_PURCHASE_RATE);
    if (purchaseRate > 0) {
      const mcReduction   = mtlSurplusFine * purchaseRate;
      mtlEffectiveMcDue   = Math.max(0, round2(mtlRemainingMc - mcReduction));
    }
  } else if (mtlSurplusAmount > 0) {
    const saleRate = parseNum(liveRates.METAL_SALE_RATE);
    if (saleRate > 0) {
      const fineReduction  = mtlSurplusAmount / saleRate;
      mtlEffectiveFineDue  = Math.max(0, round3(mtlRemainingFine - fineReduction));
    }
  }

  const isMetalSettlementComplete =
    !hasMetal || (mtlEffectiveFineDue === 0 && mtlEffectiveMcDue === 0);

  // ──────────────────────────────────────────────────────────────────────────
  // VALIDATION
  // ──────────────────────────────────────────────────────────────────────────

  const isRtgsValid = () => {
    if (!hasRTGS) return true;
    if (!isRtgsCompleted) return false;
    if (rtgsMetalEntries.some((e) => parseNum(e.gross) > 0 && !e.billNo.trim())) return false;
    if (rtgsPhysicalAmount > 0 && !rtgsCollectionReceipt.trim()) return false;
    if (rtgsBankingTotal > 0) {
      if (rtgsBankingEntries.some((e) => parseNum(e.amount) > 0 && !e.utr.trim())) return false;
    }
    return true;
  };

  const isMetalValid = () => {
    if (!hasMetal) return true;
    if (!isMetalSettlementComplete) return false;
    if (mtlMetalEntries.some((e) => parseNum(e.gross) > 0 && parseNum(e.purity) <= 0)) return false;
    return true;
  };

  const canSubmit = isRtgsValid() && isMetalValid();

  // ──────────────────────────────────────────────────────────────────────────
  // TOTALS SUMMARY
  // ──────────────────────────────────────────────────────────────────────────

  const totalFineWeightDue  = round3(rtgsMetalDue + mtlMetalDue);
  const totalAmountDue      = round2(rtgsCashDue  + mtlCashDue);
  const totalFineWtDelivered = round3(rtgsFineEntered + mtlFineEntered);
  const totalAmountPaid      = round2(rtgsAmountEntered + mtlAmountEntered);
  const totalOrders          = orders.length;

  // ──────────────────────────────────────────────────────────────────────────
  // RTGS METAL HELPERS
  // ──────────────────────────────────────────────────────────────────────────

  const addRtgsMetalEntry    = () => setRtgsMetalEntries((p) => [...p, { gross: "", purity: "", billNo: "" }]);
  const removeRtgsMetalEntry = (i) => setRtgsMetalEntries((p) => p.filter((_, idx) => idx !== i));
  const updateRtgsMetalEntry = (i, field, value) => {
    let v = value;
    if (field === "purity") { const n = parseFloat(value); if (Number.isFinite(n) && n > 100) v = "100"; }
    setRtgsMetalEntries((p) => p.map((e, idx) => idx === i ? { ...e, [field]: v } : e));
  };

  // ──────────────────────────────────────────────────────────────────────────
  // METAL BUCKET HELPERS
  // ──────────────────────────────────────────────────────────────────────────

  const addMtlMetalEntry    = () => setMtlMetalEntries((p) => [...p, { gross: "", purity: "", billNo: "" }]);
  const removeMtlMetalEntry = (i) => setMtlMetalEntries((p) => p.filter((_, idx) => idx !== i));
  const updateMtlMetalEntry = (i, field, value) => {
    let v = value;
    if (field === "purity") { const n = parseFloat(value); if (Number.isFinite(n) && n > 100) v = "100"; }
    setMtlMetalEntries((p) => p.map((e, idx) => idx === i ? { ...e, [field]: v } : e));
  };

  // ──────────────────────────────────────────────────────────────────────────
  // BANKING HELPERS
  // ──────────────────────────────────────────────────────────────────────────

  const addBankingEntry    = () => setRtgsBankingEntries((p) => [...p, { amount: "", utr: "" }]);
  const removeBankingEntry = (i) => setRtgsBankingEntries((p) => p.filter((_, idx) => idx !== i));
  const updateBankingEntry = (i, field, value) =>
    setRtgsBankingEntries((p) => p.map((e, idx) => idx === i ? { ...e, [field]: value } : e));

  // ──────────────────────────────────────────────────────────────────────────
  // CONFIRM SETTLEMENT
  // Builds payload and calls onConfirm — the parent (PaymentPendingCard) then
  // persists to Firestore and manages the subsequent JRE assign + OTP flow.
  // ──────────────────────────────────────────────────────────────────────────

  const handleSettle = async () => {
    if (!canSubmit || isProcessing) return;

    setIsProcessing(true);
    try {
      const settlementPayload = {
        // Meta
        sellerId:    seller,
        operatorId:  group.operatorId || orders[0]?.operatorId || localStorage.getItem("operatorId") || "",
        orderIds:    orders.map((o) => o.docId || o.id),
        ordersCount: orders.length,

        // Summary — seller-table totals (fine + amount)
        totalFine: totalFineWeightDue,
        totalAmount: totalAmountDue,
        totalFineWeightDue,
        totalAmountDue,
        totalFineWtDelivered,
        totalAmountPaid,

        // Rates snapshot
        ratesUsed: { ...liveRates },

        // RTGS bucket — includes group totals and this bucket's fine + amount
        rtgsBucket: hasRTGS ? {
          totalFine: totalFineWeightDue,
          totalAmount: totalAmountDue,
          dueFineWt:  rtgsMetalDue,
          dueAmount:  rtgsCashDue,
          metalEntries: rtgsMetalEntries
            .filter((e) => parseNum(e.gross) > 0)
            .map((e) => ({
              grossWt: parseNum(e.gross),
              purity:  parseNum(e.purity),
              fineWt:  round3((parseNum(e.gross) * parseNum(e.purity)) / 100),
              billNo:  e.billNo.trim(),
            })),
          calculatedFineWt: rtgsFineEntered,
          physicalCash: {
            amount:              rtgsPhysicalAmount,
            collectionReceiptNo: rtgsCollectionReceipt.trim(),
          },
          banking: {
            totalAmount: rtgsBankingTotal,
            entries: rtgsBankingEntries
              .filter((e) => parseNum(e.amount) > 0 || e.utr.trim())
              .map((e) => ({ amount: parseNum(e.amount), utr: e.utr.trim() })),
          },
          totalAmountEntered: rtgsAmountEntered,
          paymentMode:
            rtgsPhysicalAmount > 0 && rtgsBankingTotal > 0 ? "physical_and_banking" :
            rtgsPhysicalAmount > 0 ? "physical" :
            rtgsBankingTotal   > 0 ? "banking"  : "none",
          spilloverToMetal: { fineWt: rtgsSpilloverFine, amount: rtgsSpilloverAmount },
          effectiveFineDue:  rtgsEffectiveFineDue,
          effectiveMcDue:    rtgsEffectiveMcDue,
          settlementComplete: isRtgsCompleted,
        } : null,

        // Metal bucket — includes group totals and this bucket's fine + amount
        metalBucket: hasMetal ? {
          totalFine: totalFineWeightDue,
          totalAmount: totalAmountDue,
          dueFineWt: mtlMetalDue,
          dueAmount: mtlCashDue,
          metalEntries: mtlMetalEntries
            .filter((e) => parseNum(e.gross) > 0)
            .map((e) => ({
              grossWt: parseNum(e.gross),
              purity:  parseNum(e.purity),
              fineWt:  round3((parseNum(e.gross) * parseNum(e.purity)) / 100),
              billNo:  e.billNo?.trim() || null,
            })),
          calculatedFineWt: mtlFineEntered,
          amountEntered:    mtlAmountEntered,
          effectiveFineDue: mtlEffectiveFineDue,
          effectiveMcDue:   mtlEffectiveMcDue,
          settlementComplete: isMetalSettlementComplete,
        } : null,

        settledAt: new Date(),
      };

      console.log("========== SELLER SETTLEMENT PAYLOAD ==========");
      console.log(JSON.stringify(settlementPayload, null, 2));

      // Hand off to parent — parent saves to Firestore, generates ID, etc.
      await onConfirm(settlementPayload);
    } catch (err) {
      console.error("[PayModal] Settlement error:", err);
      alert(`Settlement failed: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────────────────────────────────

  return (
    <div className="pm-overlay" onClick={onClose}>
      <div className="pm-modal" onClick={(e) => e.stopPropagation()}>

        {/* ── HEADER ─────────────────────────────────────────────────── */}
        <div className="pm-header">
          <span className="pm-title">💳 Settle Payment — {seller}</span>
          <button className="pm-close" onClick={onClose} disabled={isProcessing}>
            <X size={20} />
          </button>
        </div>

        {/* ── DUE SUMMARY BAR ────────────────────────────────────────── */}
        <div className="pm-due-bar">
          <span className="pm-due-label">Seller Due</span>
          <div className="pm-due-values">
            <span>Fine Wt: <strong>{totalFineWeightDue.toFixed(3)}g</strong></span>
            <span className="pm-sep">·</span>
            <span>Amount: <strong>₹{totalAmountDue.toFixed(2)}</strong></span>
            <span className="pm-sep">·</span>
            <span>Orders: <strong>{totalOrders}</strong></span>
          </div>
        </div>

        <div className="pm-body">

          {/* ═══════════════════════════════════════════════════════════
              RTGS BUCKET
          ═══════════════════════════════════════════════════════════ */}
          {hasRTGS && (
            <section className="pm-bucket">
              <div className="pm-bucket-header">
                <h4 className="pm-bucket-title">RTGS Bucket</h4>
                {isRtgsCompleted && (
                  <span className="pm-badge pm-badge--done">
                    <CheckCircle2 size={13} /> Completed
                  </span>
                )}
              </div>

              <div className="pm-rates-row">
                <span className="pm-rate pm-rate--sale">Sale ₹{liveRates.RTGS_SALE_RATE}/g</span>
                <span className="pm-rate pm-rate--buy">Purchase ₹{liveRates.RTGS_PURCHASE_RATE}/g</span>
              </div>

              <div className="pm-dues-row">
                <span>Fine Wt Due: <strong>{rtgsMetalDue.toFixed(3)}g</strong></span>
                <span>Amount Due: <strong>₹{rtgsCashDue.toFixed(2)}</strong></span>
              </div>

              {/* Fine Wt details */}
              <div className="pm-section-label">Fine Wt Details</div>
              <div className="pm-entries">
                {rtgsMetalEntries.map((entry, i) => (
                  <div key={i} className="pm-entry-row">
                    <div className="pm-field">
                      <label>Gross (g)</label>
                      <input type="number" min="0" step="0.001" value={entry.gross}
                        onChange={(e) => updateRtgsMetalEntry(i, "gross", e.target.value)}
                        placeholder="0.000" className="pm-input" />
                    </div>
                    <div className="pm-field">
                      <label>Purity (%)</label>
                      <input type="number" min="0" max="100" step="0.01" value={entry.purity}
                        onChange={(e) => updateRtgsMetalEntry(i, "purity", e.target.value)}
                        placeholder="99.50" className="pm-input" />
                    </div>
                    <div className="pm-field">
                      <label>Bill No <span className="pm-req">*</span></label>
                      <input type="text" value={entry.billNo}
                        onChange={(e) => updateRtgsMetalEntry(i, "billNo", e.target.value)}
                        placeholder="Bill #" className="pm-input" />
                    </div>
                    {rtgsMetalEntries.length > 1 && (
                      <button type="button" className="pm-btn-remove" onClick={() => removeRtgsMetalEntry(i)}>
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="pm-btn-add" onClick={addRtgsMetalEntry}>
                  <Plus size={13} /> Add Metal Entry
                </button>
              </div>
              <div className="pm-calc-out">
                Calculated Fine Wt: <strong>{rtgsFineEntered.toFixed(3)}g</strong>
              </div>

              {/* Amount details */}
              <div className="pm-section-label">Amount Details</div>

              {/* Physical Cash */}
              <div className="pm-cash-method">
                <div className="pm-method-title"><Banknote size={15} /><span>Physical Cash</span></div>
                <div className="pm-grid-2">
                  <div className="pm-field">
                    <label>Amount (₹)</label>
                    <input type="number" min="0" step="0.01" value={rtgsCashAmount}
                      onChange={(e) => setRtgsCashAmount(e.target.value)}
                      placeholder="₹0.00" className="pm-input" />
                  </div>
                  <div className="pm-field">
                    <label>Collection Receipt No <span className="pm-req">*</span></label>
                    <input type="text" value={rtgsCollectionReceipt}
                      onChange={(e) => setRtgsCollectionReceipt(e.target.value)}
                      placeholder="Receipt No" className="pm-input" />
                  </div>
                </div>
              </div>

              {/* Banking */}
              <div className="pm-cash-method">
                <div className="pm-method-title"><Landmark size={15} /><span>Banking</span></div>
                {rtgsBankingEntries.map((entry, i) => (
                  <div key={i} className="pm-entry-row">
                    <div className="pm-field" style={{ flex: 1 }}>
                      <label>Amount (₹)</label>
                      <input type="number" min="0" step="0.01" value={entry.amount}
                        onChange={(e) => updateBankingEntry(i, "amount", e.target.value)}
                        placeholder="₹0.00" className="pm-input" />
                    </div>
                    <div className="pm-field" style={{ flex: 1 }}>
                      <label>UTR Number <span className="pm-req">*</span></label>
                      <input type="text" value={entry.utr}
                        onChange={(e) => updateBankingEntry(i, "utr", e.target.value)}
                        placeholder="UTR Number" className="pm-input" />
                    </div>
                    {rtgsBankingEntries.length > 1 && (
                      <button type="button" className="pm-btn-remove" onClick={() => removeBankingEntry(i)}>
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="pm-btn-add" onClick={addBankingEntry}>
                  <Plus size={13} /> Add UTR Entry
                </button>
              </div>

              <div className="pm-calc-out" style={{ marginTop: 12 }}>
                RTGS Amount Paid: <strong>₹{rtgsAmountEntered.toFixed(2)}</strong>
                {rtgsPhysicalAmount > 0 && rtgsBankingTotal > 0 && (
                  <span className="pm-sub">
                    Cash ₹{rtgsPhysicalAmount.toFixed(2)} + Banking ₹{rtgsBankingTotal.toFixed(2)}
                  </span>
                )}
              </div>
            </section>
          )}

          {/* ═══════════════════════════════════════════════════════════
              METAL BUCKET
          ═══════════════════════════════════════════════════════════ */}
          {hasMetal && (
            <section className={`pm-bucket${hasRTGS && !isRtgsCompleted ? " pm-bucket--locked" : ""}`}>
              <div className="pm-bucket-header">
                <h4 className="pm-bucket-title">Metal Bucket</h4>
                {hasRTGS && !isRtgsCompleted && (
                  <span className="pm-badge pm-badge--locked"><Lock size={11} /> Locked</span>
                )}
                {isMetalSettlementComplete && (!hasRTGS || isRtgsCompleted) && (
                  <span className="pm-badge pm-badge--done"><CheckCircle2 size={13} /> Completed</span>
                )}
              </div>

              <div className="pm-rates-row">
                <span className="pm-rate pm-rate--sale">Sale ₹{liveRates.METAL_SALE_RATE}/g</span>
                <span className="pm-rate pm-rate--buy">Purchase ₹{liveRates.METAL_PURCHASE_RATE}/g</span>
              </div>

              <div className="pm-dues-row">
                <span>Fine Wt Due: <strong>{mtlMetalDue.toFixed(3)}g</strong></span>
                <span>Amount Due: <strong>₹{mtlCashDue.toFixed(2)}</strong></span>
              </div>

              <div className="pm-section-label">Fine Wt Details</div>
              <div className="pm-entries">
                {mtlMetalEntries.map((entry, i) => (
                  <div key={i} className="pm-entry-row">
                    <div className="pm-field">
                      <label>Gross (g)</label>
                      <input type="number" min="0" step="0.001" value={entry.gross}
                        onChange={(e) => updateMtlMetalEntry(i, "gross", e.target.value)}
                        placeholder="0.000" className="pm-input"
                        disabled={hasRTGS && !isRtgsCompleted} />
                    </div>
                    <div className="pm-field">
                      <label>Purity (%)</label>
                      <input type="number" min="0" max="100" step="0.01" value={entry.purity}
                        onChange={(e) => updateMtlMetalEntry(i, "purity", e.target.value)}
                        placeholder="99.50" className="pm-input"
                        disabled={hasRTGS && !isRtgsCompleted} />
                    </div>
                    <div className="pm-field">
                      <label>Bill No</label>
                      <input type="text" value={entry.billNo}
                        onChange={(e) => updateMtlMetalEntry(i, "billNo", e.target.value)}
                        placeholder="Bill #" className="pm-input"
                        disabled={hasRTGS && !isRtgsCompleted} />
                    </div>
                    {mtlMetalEntries.length > 1 && (
                      <button type="button" className="pm-btn-remove"
                        onClick={() => removeMtlMetalEntry(i)}
                        disabled={hasRTGS && !isRtgsCompleted}>
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="pm-btn-add" onClick={addMtlMetalEntry}
                  disabled={hasRTGS && !isRtgsCompleted}>
                  <Plus size={13} /> Add Metal Entry
                </button>
              </div>
              <div className="pm-calc-out">
                Calculated Fine Wt: <strong>{mtlFineEntered.toFixed(3)}g</strong>
              </div>

              <div className="pm-section-label">Amount Details</div>
              <div className="pm-field">
                <label>Amount (₹)</label>
                <input type="number" min="0" step="0.01" value={mtlCashAmount}
                  onChange={(e) => setMtlCashAmount(e.target.value)}
                  placeholder="₹0.00" className="pm-input"
                  disabled={hasRTGS && !isRtgsCompleted} />
              </div>
            </section>
          )}

          {/* ═══════════════════════════════════════════════════════════
              TOTALS SUMMARY
          ═══════════════════════════════════════════════════════════ */}
          <section className="pm-totals">
            <div className="pm-total-row"><span>Orders</span><strong>{totalOrders}</strong></div>
            <div className="pm-total-row"><span>Fine Wt Due</span><strong>{totalFineWeightDue.toFixed(3)}g</strong></div>
            <div className="pm-total-row"><span>Amount Due</span><strong>₹{totalAmountDue.toFixed(2)}</strong></div>
            <div className="pm-total-row"><span>Fine Wt Given</span><strong>{totalFineWtDelivered.toFixed(3)}g</strong></div>
            <div className="pm-total-row"><span>Amount Paid</span><strong>₹{totalAmountPaid.toFixed(2)}</strong></div>
          </section>

          {/* Validation messages */}
          {hasMetal && !isMetalSettlementComplete && (!hasRTGS || isRtgsCompleted) && (
            <div className="pm-alert pm-alert--warn">
              Metal pending: {mtlEffectiveFineDue.toFixed(3)}g fine · ₹{mtlEffectiveMcDue.toFixed(2)} amount remaining.
            </div>
          )}
          {hasRTGS && !isRtgsCompleted && (
            <div className="pm-alert pm-alert--warn">
              RTGS pending: {rtgsEffectiveFineDue.toFixed(3)}g fine · ₹{rtgsEffectiveMcDue.toFixed(2)} amount remaining.
            </div>
          )}
          {canSubmit && (
            <div className="pm-alert pm-alert--ok">
              <CheckCircle2 size={13} style={{ verticalAlign: "middle", marginRight: 5 }} />
              Settlement complete — ready to confirm.
            </div>
          )}

          <p className="pm-rates-note">
            {ratesLoading
              ? "Loading live rates…"
              : `Rates — RTGS Sale: ₹${liveRates.RTGS_SALE_RATE}/g · Purchase: ₹${liveRates.RTGS_PURCHASE_RATE}/g · Metal Sale: ₹${liveRates.METAL_SALE_RATE}/g · Purchase: ₹${liveRates.METAL_PURCHASE_RATE}/g`}
          </p>
        </div>

        {/* ── FOOTER ─────────────────────────────────────────────────── */}
        <div className="pm-footer">
          <button className="pm-btn-cancel" onClick={onClose} disabled={isProcessing}>Cancel</button>
          <button className="pm-btn-confirm" onClick={handleSettle} disabled={!canSubmit || isProcessing}>
            {isProcessing ? "Processing…" : "✅ Confirm Settlement"}
          </button>
        </div>
      </div>

      {/* ── STYLES ─────────────────────────────────────────────────────── */}
      <style>{`
        .pm-overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.55);
          display: flex; align-items: center; justify-content: center;
          z-index: 1000; padding: 16px;
        }
        .pm-modal {
          background: #fff; border-radius: 12px;
          width: 100%; max-width: 700px; max-height: 90vh;
          display: flex; flex-direction: column;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
          overflow: hidden;
        }
        .pm-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 20px; border-bottom: 1px solid #e5e7eb;
          background: #f8fafc;
        }
        .pm-title { font-size: 17px; font-weight: 700; color: #1f2937; }
        .pm-close {
          background: none; border: none; cursor: pointer;
          color: #6b7280; padding: 4px; border-radius: 4px;
          display: flex; align-items: center;
        }
        .pm-close:hover { background: #f3f4f6; color: #111; }

        .pm-due-bar {
          display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
          padding: 10px 20px; background: #eff6ff;
          border-bottom: 1px solid #bfdbfe;
        }
        .pm-due-label { font-weight: 600; color: #1d4ed8; font-size: 13px; }
        .pm-due-values { display: flex; gap: 12px; flex-wrap: wrap; font-size: 13px; color: #374151; }
        .pm-sep { color: #d1d5db; }

        .pm-body { flex: 1; overflow-y: auto; padding: 20px; display: flex; flex-direction: column; gap: 16px; }

        .pm-bucket {
          border: 1px solid #e5e7eb; border-radius: 10px;
          padding: 16px; background: #fafafa;
        }
        .pm-bucket--locked { opacity: 0.55; pointer-events: none; }

        .pm-bucket-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
        .pm-bucket-title { font-size: 15px; font-weight: 700; color: #1f2937; margin: 0; }

        .pm-badge {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 3px 9px; border-radius: 99px; font-size: 11px; font-weight: 600;
        }
        .pm-badge--done { background: #dcfce7; color: #166534; }
        .pm-badge--locked { background: #fef3c7; color: #92400e; }

        .pm-rates-row { display: flex; gap: 10px; margin-bottom: 10px; flex-wrap: wrap; }
        .pm-rate {
          padding: 3px 9px; border-radius: 6px; font-size: 12px; font-weight: 600;
        }
        .pm-rate--sale   { background: #fef3c7; color: #92400e; }
        .pm-rate--buy    { background: #dbeafe; color: #1e40af; }

        .pm-dues-row {
          display: flex; gap: 20px; font-size: 13px; color: #374151;
          margin-bottom: 12px; flex-wrap: wrap;
        }
        .pm-section-label {
          font-size: 12px; font-weight: 600; color: #6b7280;
          text-transform: uppercase; letter-spacing: 0.5px;
          margin: 10px 0 8px;
        }
        .pm-entries { display: flex; flex-direction: column; gap: 8px; margin-bottom: 8px; }
        .pm-entry-row {
          display: flex; gap: 8px; align-items: flex-end; flex-wrap: wrap;
        }
        .pm-field {
          display: flex; flex-direction: column; gap: 4px; min-width: 120px; flex: 1;
        }
        .pm-field label { font-size: 11px; color: #6b7280; font-weight: 500; }
        .pm-req { color: #ef4444; }
        .pm-input {
          border: 1px solid #d1d5db; border-radius: 6px; padding: 6px 10px;
          font-size: 13px; width: 100%; box-sizing: border-box;
          transition: border-color 0.15s;
        }
        .pm-input:focus { outline: none; border-color: #6366f1; }
        .pm-input:disabled { background: #f9fafb; color: #9ca3af; }

        .pm-btn-add {
          display: inline-flex; align-items: center; gap: 5px;
          background: none; border: 1px dashed #d1d5db; border-radius: 6px;
          padding: 5px 12px; font-size: 12px; color: #6b7280; cursor: pointer;
          margin-top: 4px;
        }
        .pm-btn-add:hover:not(:disabled) { border-color: #6366f1; color: #6366f1; }
        .pm-btn-add:disabled { opacity: 0.4; cursor: not-allowed; }

        .pm-btn-remove {
          background: none; border: 1px solid #fca5a5; border-radius: 6px;
          padding: 6px; color: #ef4444; cursor: pointer; display: flex; align-items: center;
          flex-shrink: 0; margin-bottom: 0;
        }
        .pm-btn-remove:hover:not(:disabled) { background: #fef2f2; }
        .pm-btn-remove:disabled { opacity: 0.4; cursor: not-allowed; }

        .pm-calc-out {
          background: #f1f5f9; border-radius: 6px;
          padding: 8px 12px; font-size: 13px; color: #374151;
        }
        .pm-sub { display: block; margin-top: 3px; font-size: 11px; opacity: 0.7; }

        .pm-cash-method {
          border: 1px solid #e5e7eb; border-radius: 8px;
          padding: 12px; background: #fff; margin-bottom: 10px;
        }
        .pm-method-title {
          display: flex; align-items: center; gap: 7px;
          font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 10px;
        }
        .pm-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }

        .pm-totals {
          border: 1px solid #e5e7eb; border-radius: 10px;
          padding: 14px 16px; background: #fff;
        }
        .pm-total-row {
          display: flex; justify-content: space-between; align-items: center;
          padding: 5px 0; font-size: 13px; color: #374151;
          border-bottom: 1px solid #f3f4f6;
        }
        .pm-total-row:last-child { border-bottom: none; }

        .pm-alert {
          padding: 9px 12px; border-radius: 7px; font-size: 13px;
        }
        .pm-alert--warn { background: #fff7ed; border: 1px solid #fed7aa; color: #9a3412; }
        .pm-alert--ok   { background: #f0fdf4; border: 1px solid #bbf7d0; color: #166534; }

        .pm-rates-note { font-size: 11px; color: #9ca3af; margin: 0; text-align: center; }

        .pm-footer {
          display: flex; justify-content: flex-end; gap: 10px;
          padding: 14px 20px; border-top: 1px solid #e5e7eb;
          background: #f8fafc;
        }
        .pm-btn-cancel {
          background: #fff; border: 1px solid #d1d5db; border-radius: 8px;
          padding: 9px 20px; font-size: 14px; cursor: pointer; color: #374151;
          font-weight: 500;
        }
        .pm-btn-cancel:hover:not(:disabled) { background: #f3f4f6; }
        .pm-btn-confirm {
          background: #16a34a; border: none; border-radius: 8px;
          padding: 9px 22px; font-size: 14px; font-weight: 600;
          color: #fff; cursor: pointer;
          transition: background 0.15s;
        }
        .pm-btn-confirm:hover:not(:disabled) { background: #15803d; }
        .pm-btn-confirm:disabled { background: #9ca3af; cursor: not-allowed; }
      `}</style>
    </div>
  );
}

export default PayModal;
